import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import LegalAgreementTransformer, {
  type LegalAgreementTransformerType,
} from "@/Modules/Transformers/LegalAgreement";
import type { BotLegalAgreementWebSocket } from "@/Modules/Chat/Models/WebSocket/LegalAgreements";
import type { CustomerStore } from "@/Modules/Customers/Store";
import type { LegalAgreement } from "@/Modules/Models/Domain/LegalAgreement";

import { type UserLegalAgreementStore, useUserLegalAgreementStore } from "@/Modules/LegalAgreement/Store";
import { type ChatStore, useChatStore } from "@/Modules/Chat/Store";
import { type UserStore, useUserStore } from "@/Modules/User/Store";

import useCustomerStore from "@/Modules/Customers/Store";

import { MessageTypeWebSocket } from "@/Modules/Chat/Models/WebSocket";

import { hasValue, isObject } from "@/Utils/Objects";

export interface ExtraParamsBotStageChanged {
  userLegalAgreementsStore: UserLegalAgreementStore;
  userStore: UserStore;
  chatStore: ChatStore;
  customerStore: CustomerStore;
  legalAgreementTransformer: LegalAgreementTransformerType;
}
export const isContractChange = (message: unknown): message is BotLegalAgreementWebSocket => {
  return (
    isObject(message) &&
    (message.type === MessageTypeWebSocket.CONTRACT_SIGNED ||
      message.type === MessageTypeWebSocket.NEW_CONTRACT)
  );
};

export const botContractChangeHandler = async (
  message: BotLegalAgreementWebSocket,
  {
    userLegalAgreementsStore = useUserLegalAgreementStore(),
    chatStore = useChatStore(),
    userStore = useUserStore(),
    customerStore = useCustomerStore(),
    legalAgreementTransformer = LegalAgreementTransformer,
  }: ExtraParamsBotStageChanged
): Promise<undefined> => {
  if (message.type === MessageTypeWebSocket.CONTRACT_SIGNED) {
    const legalAgreementWebsocket = legalAgreementTransformer.toDomain(message.data);

    if (userStore.getUserIsPatient && hasValue(userStore.user) && hasValue(userStore.user?.therapistId)) {
      userLegalAgreementsStore.legalAgreements = updateLegalAgreements(
        userLegalAgreementsStore.legalAgreements,
        legalAgreementWebsocket
      );
    } else if (chatStore.getUserActiveRoom && userStore.getUserIsTherapist) {
      const customerId = chatStore.getUserActiveRoom.id;

      if (
        hasValue(customerStore.customers[customerId]) &&
        hasValue(customerStore.customers[customerId].legalAgreements)
      ) {
        customerStore.customers[customerId].legalAgreements = updateLegalAgreements(
          customerStore.customers[customerId].legalAgreements,
          legalAgreementWebsocket
        );
      }
    }
  } else {
    if (userStore.getUserIsPatient && hasValue(userStore.user) && hasValue(userStore.user?.therapistId)) {
      userLegalAgreementsStore.fetchLegalAgreements(userStore.user.id);
    } else if (chatStore.getUserActiveRoom && userStore.getUserIsTherapist) {
      const customerId = chatStore.getUserActiveRoom.id;

      userLegalAgreementsStore.fetchLegalAgreements(customerId);
    }
  }

  return Promise.resolve(undefined);
};

const updateLegalAgreements = (
  allLegalAgreements: LegalAgreement[],
  newLegalAgreement: LegalAgreement
): LegalAgreement[] => {
  return allLegalAgreements.map((la) => {
    if (la.uuid === newLegalAgreement.uuid) return newLegalAgreement;

    return la;
  });
};

export default {
  isType: isContractChange,
  handler: botContractChangeHandler,
} as EventFilter<ExtraParamsBotStageChanged>;
